import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle, Crown, Home } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your payment...');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    const capturePayment = async () => {
      // Get URL parameters
      const paymentToken = searchParams.get('token'); // PayPal returns this
      const paymentType = searchParams.get('type');
      const creatorId = searchParams.get('creator_id');
      const tierId = searchParams.get('tier_id');

      if (!paymentToken) {
        setStatus('error');
        setMessage('Payment token not found. Please try again.');
        return;
      }

      try {
        if (paymentType === 'subscription') {
          // Capture subscription payment
          const response = await axios.post(
            `${API}/subscription/capture/${paymentToken}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.subscription) {
            setStatus('success');
            setMessage('Subscription activated successfully!');
            setSubscriptionInfo(response.data.subscription);
            toast.success('Welcome! Your subscription is now active.');
          }
        } else {
          // Capture regular payment (tips, etc.)
          const response = await axios.post(
            `${API}/paypal/capture/${paymentToken}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );

          setStatus('success');
          setMessage('Payment completed successfully!');
          toast.success('Thank you for your support!');
        }
      } catch (error) {
        console.error('Payment capture error:', error);
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Failed to process payment. Please contact support.');
        toast.error('Payment processing failed');
      }
    };

    if (token) {
      capturePayment();
    }
  }, [token, searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center space-y-6">
          {/* Status Icon */}
          <div className="flex justify-center">
            {status === 'processing' && (
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
            )}
          </div>

          {/* Status Message */}
          <div>
            <h1 className="text-2xl font-bold mb-2">
              {status === 'processing' && 'Processing Payment'}
              {status === 'success' && 'Payment Successful!'}
              {status === 'error' && 'Payment Failed'}
            </h1>
            <p className="text-muted-foreground">{message}</p>
          </div>

          {/* Subscription Info */}
          {status === 'success' && subscriptionInfo && (
            <div className="p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-2xl">
              <div className="flex items-center justify-center gap-3">
                <Crown className="w-8 h-8 text-yellow-500" />
                <div className="text-left">
                  <p className="font-semibold">{subscriptionInfo.tier_name} Subscription</p>
                  <p className="text-sm text-muted-foreground">
                    ${subscriptionInfo.price}/month • Renews automatically
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {status === 'success' && (
              <Button
                onClick={() => navigate('/home')}
                className="w-full rounded-full"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Home
              </Button>
            )}
            {status === 'error' && (
              <>
                <Button
                  onClick={() => navigate(-1)}
                  className="w-full rounded-full"
                >
                  Try Again
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/support')}
                  className="w-full rounded-full"
                >
                  Contact Support
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
